<template>
    <div class="vue-calendar">
        <calendar-header
            :locale="locale"
            :disabled="disabledDays"
            :full-month-names="fullMonthNames"
            :first-day-of-month="currentMonthStart"
            @changeMonth="changeMonth"
        >
        </calendar-header>

        <div class="calendar-body">
            <div class="days-header">
                <strong class="day-label" v-for="dayNumber in 7">{{
                    (dayNumber - 1) | dayToString(locale, firstDay, fullDayNames)
                }}</strong>
            </div>

            <div class="days-container">
                <div class="week-row" v-for="week in calendar">
                    <div
                        v-for="day in week"
                        class="week-day-cell"
                        :class="{
                            today: day.isToday,
                            weekend: day.isWeekend,
                            saturday: day.isSaturday,
                            sunday: day.isSunday,
                            disabled: isDayDisabled(day),
                            highlighted: isDayHighlighted(day),
                            'not-current-month': !day.isCurrentMonth,
                        }"
                        @click.stop="dayClick(day)"
                    >
                        <div class="day-number">
                            {{ day.monthDay }}
                        </div>
                        <events-box
                            :events="day.events"
                            :day="day"
                            :show-limit="showLimit"
                            @eventClicked="eventClicked"
                            @showMore="showEventsModal"
                        ></events-box>
                    </div>
                </div>
            </div>
        </div>

        <events-modal v-if="showModal" :events="currentEventsList" @hideModal="hideEventsModal"></events-modal>
    </div>
</template>
<script>
import eventsBox from './events-box.vue';
import eventsModal from './events-modal.vue';
import calHeader from './calendar-header.vue';
import dateHelper from './utils/calendar-helpers';

export default {
    props: {
        events: {
            type: Array,
            default: () => [],
        },
        firstDay: {
            type: Number | String,
            default: 0,
        },
        fullMonthNames: {
            type: Boolean,
            default: true,
        },
        fullDayNames: {
            type: Boolean,
            default: false,
        },
        showLimit: {
            type: Number,
            default: 3,
        },
        moreText: {
            type: String,
            default: 'Show more',
        },
        disabled: {
            type: Object,
            default: () => {
                return {};
            },
        },
        highlight: {
            type: Object,
            default: () => {
                return {};
            },
        },
    },
    data() {
        return {
            showModal: false,
            currentEventsList: null,
            disabledDays: this.disabled,
            highlightDays: this.highlight,
            currentMonthStart: dateHelper.firstDateOfMonth(),
        };
    },
    computed: {
        calendar() {
            return dateHelper.buildCalendar(this.currentMonthStart, this.events, this.firstDay);
        },
        locale() {
            return this.$store.state.interfaceLanguage;
        },
    },
    watch: {
        disabled(newObj) {
            this.disabledDays = newObj;
        },
        highlight(newObj) {
            this.highlightDays = newObj;
        },
    },
    methods: {
        changeMonth(monthStart) {
            this.currentMonthStart = monthStart;
            let monthEnd = dateHelper.lastDateOfMonth(new Date(monthStart));
            this.$emit('monthChanged', monthStart, monthEnd);
        },
        dayClick(day) {
            this.$emit('dayClicked', day);
        },
        eventClicked(event) {
            this.$emit('eventClicked', event);
        },
        showEventsModal(events) {
            this.currentEventsList = events;
            this.showModal = true;
        },
        hideEventsModal() {
            this.showModal = false;
        },
        isDayHighlighted(date) {
            if (!this.highlightDays) return false;

            if (this.highlightDays.dates) {
                return this.highlightDays.dates.some(d => date.date.toDateString() === d.toDateString());
            }

            if (this.highlightDays.to && date.date < this.highlightDays.to) return true;
            if (this.highlightDays.from && date.date > this.highlightDays.from) return true;
            if (this.highlightDays.days && this.highlightDays.days.indexOf(date.getDay) !== -1) return true;
        },
        isDayDisabled(date) {
            if (!this.disabledDays) return false;

            let isDisabledDays = false;
            if (this.disabledDays.dates) {
                this.disabledDays.dates.forEach(d => {
                    if (date.date.toDateString() === d.toDateString()) {
                        isDisabledDays = true;
                        return true;
                    }
                });
            }

            if (isDisabledDays) return true;
            if (this.disabledDays.to && date.date < this.disabledDays.to) return true;
            if (this.disabledDays.from && date.date > this.disabledDays.from) return true;
            return this.disabledDays.days && this.disabledDays.days.indexOf(date.getDay) !== -1;
        },
    },
    filters: {
        dayToString(dayNumber, locale, dayOffset = 0, fullNames = 0) {
            dayNumber = parseInt(dayNumber + dayOffset) % 7;
            return dateHelper.localDayName(locale, dayNumber, fullNames);
        },
    },
    components: {
        'events-box': eventsBox,
        'events-modal': eventsModal,
        'calendar-header': calHeader,
    },
    mounted() {
        this.changeMonth(dateHelper.firstDateOfMonth());
    },
};
</script>
<style>
.vue-calendar {
    background: #fff;
    margin: 0 auto;
}

.calendar-body {
    margin: 10px 0;
}

.days-header {
    display: flex;
    border-top: none;
    border-bottom: 1px solid #ede8e4;
    border-left: none;
    padding: 5px 0;
}

.day-label {
    flex: 1;
    text-align: center;
    border-right: none;
}

.day-number {
    color: #67635f;
    display: flex;
    font-size: 18px;
    justify-content: center;
    margin: 0;
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

.week-row {
    border-left: 1px solid #ede8e4;
    display: flex;
}

.week-day-cell {
    cursor: pointer;
    position: relative;
    flex: 1;
    height: 60px;
    padding: 0;
    border-right: 1px solid #ede8e4;
    border-bottom: 1px solid #ede8e4;
}

.week-day-cell.disabled {
    background-color: rgb(245, 245, 245);
}

.week-day-cell.not-current-month > .day-number {
    color: rgb(195, 195, 195);
}

.week-day-cell.today > .day-number {
    color: white;
}

.week-day-cell.today {
    background: url('../../../assets/images/circle-xxl.png') no-repeat;
    background-size: 71%;
    background-position: center;
}

.last-period-day {
    background-color: #cf004a;
    border-radius: 0;
    color: #fff;
    width: 100%;
    height: 60px;
    position: absolute;
}

.selected-day,
.disabled-day,
.allowed-day {
    width: 50px;
    height: 50px;
    margin: 3px auto;
}

.selected-day {
    background-color: #cf004a;
    border-radius: 50%;
    border: 2px solid #cf004a;
}

.disabled-day {
    border-radius: 50%;
    border: 2px solid #cf004a;
}

.selected-day .event,
.disabled-day .event,
.last-examination-day .event,
.last-period-day .event,
.allowed-tmp-day .event,
.allowed-day .event {
    background-color: transparent;
}

.selected-day .day-number,
.last-examination-day .day-number,
.last-period-day .day-number,
.allowed-tmp-day .day-number,
.allowed-day .day-number {
    font-weight: 700;
}

.selected-day .day-number {
    color: #fff;
}

.last-period-day .day-number {
    color: #fff;
}

.allowed-tmp-day .day-number {
    color: #206718;
}

.today .allowed-tmp-day .day-number {
    color: #fff;
}

.period-day-prediction .day-number {
    color: #cf004a;
}

.today .period-day-prediction .day-number {
    color: #fff;
}

.allowed-day .day-number {
    color: #fff;
}

.last-examination-day .day-number {
    color: #cf004a;
    font-size: 16px;
    margin-top: 14px;
}

.allowed-tmp-day {
    border: 1px dashed #0e9604;
    background-color: rgba(14, 150, 4, 0.2);
    margin: 3px 0;
    width: 100%;
}

.period-day-prediction .event {
    background-color: transparent;
}

.period-day-prediction {
    border: 1px dashed #cf004a;
    background-color: rgba(150, 0, 3, 0.2);
    margin: 3px 0;
    width: 100%;
}

.selected-period-day {
    border: 2px solid #cf004a;
}

.selected-period-day .event {
    background-color: transparent;
}

.allowed-day {
    border: 1px dashed #0e9604;
    background-color: rgba(14, 150, 4, 1);
    margin: 3px 0;
    width: 100%;
}

.today .events .allowed-day {
    background: rgba(14, 150, 4, 1) url('../../../assets/images/circle-xxl.png') no-repeat;
    background-size: 71%;
    background-position: center;
}

.today .events .last-period-day {
    background: #cf004a url('../../../assets/images/circle-xxl.png') no-repeat;
    background-size: 71%;
    background-position: center;
}

.last-examination-day {
    border: 2px solid #cf004a;
    background: url('../../../assets/images/braster-logo-app.png') #fff 50% 30% no-repeat;
    background-size: 35%;
    height: 60px;
    position: absolute;
    width: 100%;
    z-index: 1;
}

@media (max-width: 768px) {
    .vue-calendar {
        margin-bottom: 30px;
    }

    .selected-day,
    .disabled-day {
        max-width: 50px;
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// calendar.vue?65de8eda